import { getTrackShipmentRoute, ReservedRoutes } from '@/lib/routing';

export type HrefRouteName =
  | Exclude<
      keyof typeof ReservedRoutes,
      'Sandbox' | 'WithdrawalReturnsFinland' | 'BeforeDeliveryReclaimFinland'
    >
  | 'Page';

type RouteParams = {
  Root: undefined;
  Search: undefined;
  Product: { id: string; slug: string };
  Category: { slugSegments: string[] };
  MyStadium: { slugSegments: string[] } | undefined;
  Checkout: { slugSegments: string[] } | undefined;
  Club: { sectionId: string; slug: string } | undefined;
  Brand: { slug: string } | undefined;
  Faq: { slug: string };
  CustomerService: { slugSegments: string[] } | undefined;
  Store: { id: string } | undefined;
  Page: { slugSegments: string[] };
  StoryPreview: { slugSegments: string[] } | undefined;
  Sitemap: undefined;
  OnlineReturns: undefined;
  WithdrawalReturns: { market: 'se' | 'fi' };
  BeforeDeliveryReclaim: { market: 'se' | 'fi' };
  TrackShipment: { market: 'se' | 'fi' | 'no' };
};

export type RouteParamsByName = { [K in HrefRouteName]: RouteParams[K] };

type ParamsFor<K extends keyof RouteParamsByName> = Exclude<
  RouteParamsByName[K],
  undefined
>;

type HrefArgs<K extends HrefRouteName> = undefined extends RouteParamsByName[K]
  ? [route: K, params?: ParamsFor<K>]
  : [route: K, params: ParamsFor<K>];

const joinPath = (...segments: string[]) => {
  const encoded = segments
    .filter((segment) => segment.length > 0)
    .map((segment) => encodeURIComponent(segment));

  if (encoded.length === 0) {
    return '/';
  }

  return `/${encoded.join('/')}`;
};

export function href<K extends HrefRouteName>(...args: HrefArgs<K>) {
  const [route, params] = args as [
    HrefRouteName,
    RouteParamsByName[HrefRouteName],
  ];

  switch (route) {
    case 'Root': {
      return '/';
    }

    case 'Product': {
      const productParams = params as ParamsFor<'Product'>;
      return joinPath(
        ReservedRoutes.Product,
        productParams.id,
        productParams.slug,
      );
    }

    case 'Category': {
      const categoryParams = params as ParamsFor<'Category'>;
      return joinPath(ReservedRoutes.Category, ...categoryParams.slugSegments);
    }

    case 'Search': {
      return joinPath(ReservedRoutes.Search);
    }

    case 'MyStadium': {
      const myStadiumParams = params as ParamsFor<'MyStadium'> | undefined;
      return joinPath(
        ReservedRoutes.MyStadium,
        ...(myStadiumParams?.slugSegments ?? []),
      );
    }

    case 'Checkout': {
      const checkoutParams = params as ParamsFor<'Checkout'> | undefined;
      return joinPath(
        ReservedRoutes.Checkout,
        ...(checkoutParams?.slugSegments ?? []),
      );
    }

    case 'Club': {
      const clubParams = params as ParamsFor<'Club'> | undefined;

      if (!clubParams) {
        return joinPath(ReservedRoutes.Club);
      }

      if (!clubParams.sectionId || !clubParams.slug) {
        throw new Error(
          'Club href requires both sectionId and slug when params are provided',
        );
      }

      return joinPath(
        ReservedRoutes.Club,
        clubParams.sectionId,
        clubParams.slug,
      );
    }

    case 'Brand': {
      const brandParams = params as ParamsFor<'Brand'> | undefined;
      return brandParams?.slug
        ? joinPath(ReservedRoutes.Brand, brandParams.slug)
        : joinPath(ReservedRoutes.Brand);
    }

    case 'Faq': {
      const faqParams = params as ParamsFor<'Faq'>;
      return joinPath(...ReservedRoutes.Faq.split('/'), faqParams.slug);
    }

    case 'CustomerService': {
      const customerServiceParams = params as
        | ParamsFor<'CustomerService'>
        | undefined;

      return customerServiceParams?.slugSegments
        ? joinPath(
            ReservedRoutes.CustomerService,
            ...customerServiceParams.slugSegments,
          )
        : joinPath(ReservedRoutes.CustomerService);
    }

    case 'Store': {
      const storeParams = params as ParamsFor<'Store'> | undefined;

      return storeParams?.id
        ? joinPath(ReservedRoutes.Store, storeParams.id)
        : joinPath(ReservedRoutes.Store);
    }

    case 'Sitemap': {
      return joinPath(ReservedRoutes.Sitemap);
    }

    case 'StoryPreview': {
      const previewParams = params as ParamsFor<'StoryPreview'> | undefined;

      return previewParams
        ? joinPath(ReservedRoutes.StoryPreview, ...previewParams.slugSegments)
        : joinPath(ReservedRoutes.StoryPreview);
    }

    case 'OnlineReturns': {
      return joinPath(ReservedRoutes.OnlineReturns);
    }

    case 'WithdrawalReturns': {
      const withdrawalReturnsParams = params as ParamsFor<'WithdrawalReturns'>;
      return joinPath(
        withdrawalReturnsParams.market === 'fi'
          ? ReservedRoutes.WithdrawalReturnsFinland
          : ReservedRoutes.WithdrawalReturns,
      );
    }

    case 'BeforeDeliveryReclaim': {
      const beforeDeliveryParams = params as ParamsFor<'BeforeDeliveryReclaim'>;
      return joinPath(
        beforeDeliveryParams.market === 'fi'
          ? ReservedRoutes.BeforeDeliveryReclaimFinland
          : ReservedRoutes.BeforeDeliveryReclaim,
      );
    }
    case 'TrackShipment': {
      const trackShipmentParams = params as ParamsFor<'TrackShipment'>;

      return joinPath(getTrackShipmentRoute(trackShipmentParams.market));
    }

    default: {
      const pageParams = params as ParamsFor<'Page'>;
      return joinPath(...pageParams.slugSegments);
    }
  }
}
